import { createContext, useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import { GetUserAccountStatus, GetUserById } from "../services/backend";
import { syncStripeSubscription } from "../services/stripeCheckout";
import {
  getGoogleSubFromSession,
  getSupabaseSession,
  signOutSupabase,
} from "../services/supabase/authSession";

export const AuthContext = createContext({
  isAccountActive: false,
  isLoading: true,
  isProfileComplete: false,
  authenticatedUser: null,
  authenticateUser: async () => null,
  markProfileComplete: () => {},
  logoutUser: () => {},
});

function persistUser(user) {
  if (!user) {
    localStorage.removeItem("user");
    localStorage.removeItem("userId");
    return;
  }
  localStorage.setItem("user", JSON.stringify(user));
  localStorage.setItem("userId", user.id);
}

function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function AuthContextProvider({ children }) {
  const [userStatus, setUserStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  const applyUserSync = useCallback((user) => {
    flushSync(() => {
      setAuthenticatedUser(user);
      setIsProfileComplete(!!user?.isProfileComplete);
      if (user?.isActive === false) {
        setUserStatus(false);
      } else if (user) {
        setUserStatus(true);
      }
    });
    persistUser(user);
  }, []);

  const loadUser = useCallback(async (user, { skipStripeSync = false } = {}) => {
    if (!user?.id) {
      flushSync(() => {
        setAuthenticatedUser(null);
        setIsProfileComplete(false);
        setUserStatus(false);
      });
      persistUser(null);
      return null;
    }

    // Apply immediately so ProtectedRoute can't bounce mid-refresh.
    applyUserSync(user);

    let resolvedUser = user;

    try {
      const freshUser = await GetUserById(user.id);
      if (freshUser) resolvedUser = freshUser;

      if (!skipStripeSync) {
        try {
          await syncStripeSubscription(user.id);
          const syncedUser = await GetUserById(user.id);
          if (syncedUser) resolvedUser = syncedUser;
        } catch (error) {
          console.warn("Subscription sync skipped:", error);
        }
      }
    } catch (error) {
      console.error("Failed to refresh user profile:", error);
    }

    // Never downgrade a just-completed profile if a stale read returns incomplete.
    if (user.isProfileComplete && resolvedUser && !resolvedUser.isProfileComplete) {
      resolvedUser = { ...resolvedUser, isProfileComplete: true };
    }

    applyUserSync(resolvedUser);

    try {
      const accountStatus = await GetUserAccountStatus(resolvedUser.id);
      setUserStatus(accountStatus?.status !== false);
    } catch (error) {
      console.error("Failed to fetch user status:", error);
      setUserStatus(resolvedUser.isActive !== false);
    }

    return resolvedUser;
  }, [applyUserSync]);

  useEffect(() => {
    async function initAuth() {
      try {
        const session = await getSupabaseSession();
        const googleSub = await getGoogleSubFromSession(session);
        const stored = readStoredUser();

        // After RLS lockdown, writes require a Supabase session. Force re-login if missing.
        if (!session || !googleSub) {
          if (stored) {
            persistUser(null);
          }
          return;
        }

        const userId = stored?.id && stored.id === googleSub ? stored.id : googleSub;
        await loadUser(stored?.id === userId ? stored : { id: userId });
      } catch (error) {
        console.error("Failed to initialize auth:", error);
        persistUser(null);
      } finally {
        setIsLoading(false);
      }
    }
    initAuth();
  }, [loadUser]);

  function authenticateUser(user, options) {
    return loadUser(user, options);
  }

  function markProfileComplete(user) {
    const next = {
      ...(user || authenticatedUser || readStoredUser() || {}),
      isProfileComplete: true,
    };
    applyUserSync(next);
    return next;
  }

  function logoutUser() {
    flushSync(() => {
      setAuthenticatedUser(null);
      setIsProfileComplete(false);
      setUserStatus(false);
    });
    persistUser(null);
    signOutSupabase().catch(() => {});
  }

  const value = {
    isAccountActive: userStatus,
    isLoading,
    isProfileComplete,
    authenticatedUser,
    authenticateUser,
    markProfileComplete,
    logoutUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContextProvider;
